import { Plan } from '@wix/ambassador-checkout-server/types';
import { ReservedLocationIds } from '@wix/bookings-uou-types';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { CalendarStatus } from '../../components/BookingCalendar/ViewModel/widgetViewModel/widgetViewModel';
import { FilterTypes } from '../../components/BookingCalendar/ViewModel/filterViewModel/filterViewModel';
import { EmptyStateType } from '../../components/BookingCalendar/ViewModel/emptyStateViewModel/emptyStateViewModel';
import { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { BookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/types';
import { Optional, SlotsStatus } from '../../types/types';
import { getValidPurchasedPricingPlansForService } from '../pricingPlans/pricingPlans';
import { Balance } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { Booking } from '@wix/bookings-checkout-api';
import { extractQueryParamValues } from '../queryParams/queryParams';
import { Experiments, IWixAPI } from '@wix/yoshi-flow-editor';
import { DO_NOT_CARE_STAFF } from '../../constants/constants';
import {
  getUrlQueryParamValue,
  BookingsQueryParams,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import type { CalendarSelections } from '@wix/bookings-catalog-calendar-viewer-utils';
import { getLocalDateTimeStartOfDay } from '../dateAndTime/dateAndTime';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  getServicePricingPlanIds,
  isAppointmentService,
  isBookFlowSettingsEnabled,
  isStaffMemberConnectedToService,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  calculatePreferencesViewForAppointment,
  PreferenceToDisplayMapping,
} from '@wix/bookings-services-preferences-modal/utils';
import { initializeSelectedBookingPreferences } from '../initializeSelectedBookingPreferences/initializeSelectedBookingPreferences';
import { initializeSelectedVariantsOptions } from '../initializeSelectedVariantsOptions/initializeSelectedVariantsOptions';

export type BookingDetailsPreferenceVisibility = {
  staffMember: boolean;
  location: boolean;
  duration: boolean;
};

export function createInitialState({
  wixCodeApi,
  servicesInView,
  rescheduleBookingDetails,
  initialErrors,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
  servicesVariants,
  experiments,
  calendarSelections,
  bookingsSettings,
  isDateAndTimeViewMode,
}: {
  servicesInView: Service[];
  wixCodeApi: IWixAPI;
  rescheduleBookingDetails?: Booking;
  initialErrors: EmptyStateType[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
  servicesVariants?: ServiceOptionsAndVariants[];
  experiments: Experiments;
  calendarSelections?: CalendarSelections;
  bookingsSettings: BookingsSettings;
  isDateAndTimeViewMode: boolean;
}): CalendarState {
  const isBookFlowSettings = isBookFlowSettingsEnabled({
    experiments,
    bookingsSettings,
  });
  const locationFilterOptions = initializeLocationFilterOptions({
    wixCodeApi,
    calendarSelections,
    experiments,
    rescheduleBookingDetails,
    servicesInView,
  });
  const staffFilterOptions = initializeStaffMembersFilterOptions({
    wixCodeApi,
    servicesInView,
    experiments,
    calendarSelections,
  });
  const purchasedPricingPlans = initializePurchasedPricingPlans({
    servicesInView,
    allPurchasedPricingPlans,
    isPricingPlanInstalled,
    isUserLoggedIn,
  });
  const selectedDate = initializeSelectedDate(wixCodeApi);

  const serviceVariantsMap = createServicesVariantsMap(servicesVariants);

  const appointmentPreferencesDisplay = calculateAppointmentPreferencesDisplay({
    servicesInView,
    serviceVariantsMap,
    bookingsSettings,
    experiments,
    isDateAndTimeViewMode,
  });

  const selectedBookingPreferences =
    isBookFlowSettings && !isDateAndTimeViewMode
      ? initializeSelectedBookingPreferences({
          calendarSelections,
          servicesInView,
        })
      : [];

  const selectedVariantsOptions = isBookFlowSettings && !isDateAndTimeViewMode
    ? initializeSelectedVariantsOptions({
        calendarSelections,
        servicesInView,
        serviceVariantsMap,
      })
    : [];

  return {
    calendarStatus: CalendarStatus.INITIALIZING,
    slotsStatus: SlotsStatus.LOADING,
    servicesInView,
    selectedDate,
    selectedTimezone: undefined,
    selectedTime: undefined,
    availableSlots: undefined,
    selectableSlotsAtSelectedTime: undefined,
    selectedRange: undefined,
    availableSlotsPerDay: undefined,
    selectedBookingPreferences,
    calendarErrors: [],
    rescheduleBookingDetails,
    dialog: undefined,
    filterOptions: {
      [FilterTypes.SERVICE]: [],
      [FilterTypes.LOCATION]: locationFilterOptions,
      [FilterTypes.STAFF_MEMBER]: staffFilterOptions,
    },
    initialErrors,
    purchasedPricingPlans,
    isUserLoggedIn,
    selectedVariantsOptions,
    serviceVariantsMap,
    appointmentPreferencesDisplay,
  };
}

const createServicesVariantsMap = (
  serviceVariants?: ServiceOptionsAndVariants[],
) => {
  if (!serviceVariants) {
    return {};
  }

  return serviceVariants.reduce<Record<string, ServiceOptionsAndVariants>>(
    (acc, serviceVariant) => {
      acc[serviceVariant.serviceId!] = serviceVariant;
      return acc;
    },
    {},
  );
};

const initializeLocationFilterOptions = ({
  calendarSelections,
  wixCodeApi,
  experiments,
  rescheduleBookingDetails,
  servicesInView,
}: {
  calendarSelections?: CalendarSelections;
  wixCodeApi: IWixAPI;
  experiments?: Experiments;
  rescheduleBookingDetails?: Booking;
  servicesInView: Service[];
}): string[] => {
  const preSelectedLocations = calendarSelections?.location
    ? [calendarSelections.location]
    : extractQueryParamValues({
        wixCodeApi,
        queryParam: BookingsQueryParams.LOCATION,
      });

  if (
    rescheduleBookingDetails &&
    experiments?.enabled('specs.bookings.RescheduleDefaultLocation')
  ) {
    const rescheduledLocationId =
      rescheduleBookingDetails.bookedEntity?.slot?.location?.id;

    const isLocationValid =
      rescheduledLocationId &&
      servicesInView[0]?.locations?.some(
        (location) => location.id === rescheduledLocationId,
      );

    if (isLocationValid) {
      preSelectedLocations.push(rescheduledLocationId);
    }
  }

  return preSelectedLocations.filter(
    (location) => location !== ReservedLocationIds.OTHER_LOCATIONS,
  );
};

const initializeStaffMembersFilterOptions = ({
  servicesInView,
  calendarSelections,
  wixCodeApi,
}: {
  servicesInView: Service[];
  wixCodeApi: IWixAPI;
  experiments?: Experiments;
  calendarSelections?: CalendarSelections;
}): string[] => {
  const staffFilterOptions: string[] = [];
  const { services } = calendarSelections || {};
  if (services?.length === 1 && services[0]?.resources?.[0]) {
    staffFilterOptions.push(services[0].resources[0]);
    return staffFilterOptions;
  }
  const resourcesIds = extractQueryParamValues({
    wixCodeApi,
    queryParam: BookingsQueryParams.RESOURCE,
  });
  if (resourcesIds && resourcesIds.includes(DO_NOT_CARE_STAFF)) {
    staffFilterOptions.push(DO_NOT_CARE_STAFF);
  } else {
    staffFilterOptions.push(
      ...initializeStaffMemberFilterOptionsFromQuery({
        wixCodeApi,
        servicesInView,
      }),
    );
  }
  return staffFilterOptions;
};
const initializeStaffMemberFilterOptionsFromQuery = ({
  servicesInView,
  wixCodeApi,
}: {
  wixCodeApi: IWixAPI;
  servicesInView: Service[];
}): string[] => {
  const staffFilterOptions: string[] = [];
  const resourcesIds = extractQueryParamValues({
    wixCodeApi,
    queryParam: BookingsQueryParams.RESOURCE,
  });
  if (resourcesIds.length) {
    resourcesIds.forEach((resourceId) => {
      const isStaffMemberConnectedToSomeService = servicesInView.some(
        (service) => isStaffMemberConnectedToService(service, resourceId),
      );
      if (isStaffMemberConnectedToSomeService) {
        staffFilterOptions.push(resourceId);
      }
    });
  }
  return staffFilterOptions;
};
const initializePurchasedPricingPlans = ({
  servicesInView,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
}: {
  servicesInView: Service[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
}): Plan[] => {
  let purchasedPricingPlans: Plan[] = [];
  let servicePricingPlanIds: string[] = [];

  if (isPricingPlanInstalled) {
    if (isUserLoggedIn) {
      servicePricingPlanIds =
        (servicesInView.length &&
          getServicePricingPlanIds(servicesInView[0])) ||
        [];
      purchasedPricingPlans = getValidPurchasedPricingPlansForService({
        servicePricingPlanIds,
        allPurchasedPricingPlans: allPurchasedPricingPlans || [],
      });
    }
  }
  return purchasedPricingPlans;
};

const initializeSelectedDate = (wixCodeApi: IWixAPI): Optional<string> => {
  const selectedDate = getUrlQueryParamValue(
    wixCodeApi,
    BookingsQueryParams.DATE,
  );
  if (selectedDate) {
    return getLocalDateTimeStartOfDay(new Date(selectedDate));
  }
};

const calculateAppointmentPreferencesDisplay = ({
  servicesInView,
  serviceVariantsMap,
  bookingsSettings,
  experiments,
  isDateAndTimeViewMode,
}: {
  servicesInView: Service[];
  serviceVariantsMap: Record<string, ServiceOptionsAndVariants>;
  bookingsSettings: BookingsSettings;
  experiments: Experiments;
  isDateAndTimeViewMode: boolean;
}): PreferenceToDisplayMapping | undefined => {
  if (
    isDateAndTimeViewMode ||
    servicesInView.length !== 1 ||
    !isAppointmentService(servicesInView[0])
  ) {
    return undefined;
  }

  const service = servicesInView[0];
  const serviceOptionsAndVariants = serviceVariantsMap[service.id!];

  return calculatePreferencesViewForAppointment({
    service,
    bookingsSettings,
    experiments,
    serviceOptionsAndVariants,
  });
};
